/**
 * Phase 94 — shared "spreadsheet-speed" inline-edit + Bulk Save engine used
 * by Invoices Ledger, Entries Ledger and Petty Cash Ledger. Every cell edit
 * is tracked locally (and the row is highlighted); nothing hits the server
 * until "Bulk Save Changes" is clicked, which PATCHes every changed row in
 * one request to `endpoint`.
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { setUnsaved } from "@/lib/unsavedGuard";

const readDraft = (key) => { try { return JSON.parse(localStorage.getItem(key) || "{}") || {}; } catch { return {}; } };
const writeDraft = (key, changes) => {
  try { localStorage.setItem(key, JSON.stringify(changes)); } catch { /* storage full / disabled — best-effort */ }
};
const clearDraft = (key) => { try { localStorage.removeItem(key); } catch { /* ignore */ } };

export default function useInlineBulkEdit({ sourceRows, endpoint, idKey = "id", numericFields = [], onSaved }) {
  const [rows, setRows] = useState(sourceRows || []);
  const [changesById, setChangesById] = useState({});
  const [saving, setSaving] = useState(false);
  const guardKey = useId();
  // Autosave Drafts — unsaved edits mirror into localStorage (per ledger
  // endpoint) so an accidental refresh/close restores them on next load.
  const draftKey = `ledger-draft:${endpoint}`;
  const toastedRef = useRef(false);

  // Re-sync from the parent's freshly-fetched data — but only while there
  // are no pending local edits, so a background refresh never clobbers
  // work in progress. First sync with real rows also restores any draft.
  useEffect(() => {
    if (Object.keys(changesById).length === 0) {
      const src = sourceRows || [];
      if (src.length > 0) {
        // Idempotent restore (StrictMode runs this twice): merge whatever
        // draft is still in storage for rows that exist in this load.
        const draft = readDraft(draftKey);
        const ids = Object.keys(draft).filter((id) => src.some((r) => r[idKey] === id));
        if (ids.length > 0) {
          const kept = Object.fromEntries(ids.map((id) => [id, draft[id]]));
          setRows(src.map((r) => (kept[r[idKey]] ? { ...r, ...kept[r[idKey]] } : r)));
          setChangesById(kept);
          if (!toastedRef.current) {
            toastedRef.current = true;
            toast.info(`Restored ${ids.length} unsaved edit${ids.length === 1 ? "" : "s"} from your last session`, {
              description: "Review them, then Bulk Save or Discard.",
              duration: 6000,
            });
          }
          return;
        }
        if (Object.keys(draft).length > 0) clearDraft(draftKey);
      }
      setRows(src);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceRows]);

  useEffect(() => {
    if (Object.keys(changesById).length > 0) writeDraft(draftKey, changesById);
  }, [draftKey, changesById]);

  const updateCell = useCallback((id, field, value) => {
    setRows((prev) => prev.map((r) => (r[idKey] === id ? { ...r, [field]: value } : r)));
    setChangesById((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  }, [idKey]);

  const isDirty = (id) => Boolean(changesById[id]);
  const dirtyCount = Object.keys(changesById).length;

  // Unsaved Edit Guard — publish dirty state globally; clear on unmount.
  useEffect(() => { setUnsaved(guardKey, dirtyCount); }, [guardKey, dirtyCount]);
  useEffect(() => () => setUnsaved(guardKey, 0), [guardKey]);

  const discardAll = () => {
    setRows(sourceRows || []);
    setChangesById({});
    clearDraft(draftKey);
  };

  const bulkSave = async () => {
    const ids = Object.keys(changesById);
    if (ids.length === 0) return;
    setSaving(true);
    try {
      const updates = ids.map((id) => {
        const patch = { ...changesById[id] };
        numericFields.forEach((f) => {
          if (patch[f] !== undefined && patch[f] !== "") patch[f] = Number(patch[f]);
        });
        return { id, ...patch };
      });
      const { data } = await api.patch(endpoint, { updates });
      if (data.errors?.length) {
        toast.error(`Saved ${data.updated} · ${data.errors.length} row(s) failed — see console`);
        console.warn("Bulk save row errors:", data.errors);
      } else {
        toast.success(`Saved ${data.updated} row${data.updated === 1 ? "" : "s"}`);
      }
      setChangesById({});
      clearDraft(draftKey);
      onSaved?.();
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    } finally {
      setSaving(false);
    }
  };

  // Ctrl/Cmd+S → Bulk Save while this ledger has unsaved edits (or the cursor
  // is inside a ledger cell). The focused cell is blurred first so its
  // in-progress text is committed and included in the save.
  const saveRef = useRef(bulkSave);
  saveRef.current = bulkSave;
  const dirtyRef = useRef(dirtyCount);
  dirtyRef.current = dirtyCount;
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey || e.key.toLowerCase() !== "s") return;
      const active = document.activeElement;
      const inLedger = Boolean(active?.closest?.("table"));
      if (dirtyRef.current === 0 && !inLedger) return;
      e.preventDefault();
      if (active && active !== document.body) active.blur();
      setTimeout(() => { saveRef.current().catch(() => {}); }, 0);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return { rows, updateCell, isDirty, dirtyCount, discardAll, bulkSave, saving };
}
